/* eslint-disable import/extensions */
import {BrsData, type ProjectData} from '@unbrs/plastic-data';
import {type IRegionUN} from '@unbrs/plastic-data/src/types/project';
import {getFeed} from '../../feeds';
// Import * as settings from '../settings'
import {BrsBaseWidget} from '../base';
import {BrsPlasticMapWidget} from './pmap';
import {BrsTextBoxWidget} from './textbox';
import {BrsPlasticProjectsWidget} from './projects';
import {BrsPlasticStatisticsWidget} from './statistics';
import html from './views/pdashboard.tmpl.html';

BrsBaseWidget.registerBrsWidget(BrsTextBoxWidget);
BrsBaseWidget.registerBrsWidget(BrsPlasticProjectsWidget);
BrsBaseWidget.registerBrsWidget(BrsPlasticStatisticsWidget);
BrsBaseWidget.registerBrsWidget(BrsPlasticMapWidget);

type IProject = ProjectData.IProject;
type IName = ProjectData.IName;
type IProjectStatus = ProjectData.IProjectStatus;

const PROJECT_STATUSES: Array<{status: IProjectStatus; value: string}> = [
	{
		status: 'not_started',
		value: 'Not started',
	},
	{
		status: 'on_going',
		value: 'On going',
	},
	{
		status: 'completed',
		value: 'Completed',
	},
];

const FEED_SOURCES: Array<{name: string; value: string}> = [
	{name: 'BRS', value: 'brs'},
	{name: 'GEF', value: 'gef'},
	{name: 'UNDP', value: 'undp'},
	{name: 'IUCN', value: 'iucn'},
	{name: 'UNIDO', value: 'unido'},
	{name: 'UNEP', value: 'unep'},
	{name: 'FFEM', value: 'ffem'},
	{name: 'IMO', value: 'imo'},
	{name: 'UNCTAD', value: 'unctad'},
	{name: 'USAID', value: 'usaid'},
	{name: 'World Bank', value: 'worldbank'},
];

const REGION_SOURCES: Array<{name: IRegionUN; value: string}> = [
	{name: 'africa', value: 'African'},
	{name: 'asia-pacific', value: 'Asia-Pacific'},
	{name: 'weog', value: 'Western European and Other'},
	{name: 'grulac', value: 'Latin American and Caribbean'},
	{name: 'ee', value: 'Eastern European'},
	{name: 'other', value: 'Other'},
];

const COUNTRY_TYPE: Array<{name: string; value: string}> = [
	{name: 'Developing', value: 'Developing'},
	{name: 'Least Developed', value: 'LDC'},
	{name: 'Small Island Developing States', value: 'SIDS'},
];

/**
 * Retrieves the data source for projects.
 * @param projectsService The OData service for projects.
 * @returns The Kendo data source for projects.
 */
function getProjectsDataSource(resourceUrl: string): kendo.data.DataSource {
	return new kendo.data.DataSource({
		transport: {
			async read(options) {
				const result = await getFeed({resourceUrl});
				options.success(result);
			},
		},
	});
}

/**
 * Creates a filter function that checks if the specified field of an item matches any of the specified values.
 * @param field The field to filter on.
 * @returns A filter function that takes an array of items and an array of values, and returns a boolean indicating if any item matches the filter.
 */
function expandedFilter<T>(field: keyof T) {
	return (items: T[], values: Array<T[keyof T]>): boolean => {
		if (values.length === 0) {
			return true;
		}

		if (items !== undefined) {
			for (const item of items) {
				if (item && values.includes(item[field])) {
					return true;
				}
			}
		}

		return false;
	};
}

export type BrsPlasticProjectsDashboardWidgetOptions = {
	geojson?: GeoJSON.FeatureCollection;
	resourceUrl: string;
};

/**
 * Represents a BrsPlasticPortalWidget.
 * @class
 */
export class BrsPlasticProjectsDashboardWidget extends BrsBaseWidget {
	private readonly mapWidget: BrsPlasticMapWidget;
	private readonly woptions: BrsPlasticProjectsDashboardWidgetOptions;

	constructor(selector: Element, options?: BrsPlasticProjectsDashboardWidgetOptions) {
		super(selector, options);
		const defaultOptions: BrsPlasticProjectsDashboardWidgetOptions = {
			resourceUrl: '',
		};
		this.woptions = {...defaultOptions, ...options};
		this.element.html(html);
		const projectsDS = getProjectsDataSource(this.woptions.resourceUrl);
		const beneficiariesDS = new kendo.data.DataSource();
		const termsDS = new kendo.data.DataSource();
		const donorsDS = new kendo.data.DataSource({
			group: {field: 'category'},
		});
		const implementedByDS = new kendo.data.DataSource({
			group: {field: 'category'},
		});
		const umbrellaDS = new kendo.data.DataSource();
		$('#brs-search-by-string', this.element).kendoBrsTextBoxWidget({
			label: 'Filters',
		});
		projectsDS.bind('change', (event: kendo.data.DataSourceChangeEvent) => {
			// Only load terms, donors, and implemented by once
			if (termsDS.data().length > 0) {
				return;
			}

			termsDS.data(BrsData.getTerms(event.items as BrsData.IBrsProject[]));
			donorsDS.data(BrsData.getDonors(event.items as BrsData.IBrsProject[]));
			beneficiariesDS.data(
				BrsData.getCountries(event.items as BrsData.IBrsProject[]),
			);
			implementedByDS.data(
				BrsData.getImplementedBy(event.items as BrsData.IBrsProject[]),
			);
			const umbrellaProjects = event.items?.filter(item => (item as IProject).isUmbrella).map(item => {
				const project = item as IProject;
				return {name: project.name, value: project.id};
			}) ?? [];
			umbrellaDS.data(umbrellaProjects.sort((a, b) => a.name.localeCompare(b.name)));
		});
		let searchValue = '';
		const viewModel = kendo.observable({
			termsDS,
			beneficiariesDS,
			donorsDS,
			implementedbyDS: implementedByDS,
			umbrellaDS,
			projectsDS,
			statusesDS: PROJECT_STATUSES,
			sourcesDS: FEED_SOURCES,
			regionsDS: REGION_SOURCES,
			countryTypesDS: COUNTRY_TYPE,
			selectedMapView: 'projects',
			selectedStatuses: [],
			selectedSources: [],
			selectedRegions: [],
			selectedCountryTypes: [],
			showGlobal: false,
			showRegional: false,
			searchValue,
			onChange() {
				const selectedTerms: IName[]
					= (viewModel.get('selectedTerms') as IName[]) || [];
				const selectedDonors: IName[]
					= (viewModel.get('selectedDonors') as IName[]) || [];
				const selectedBeneficiaries: IName[]
					= (viewModel.get('selectedBeneficiaries') as IName[]) || [];
				const selectedImplementedby: IName[]
					= (viewModel.get('selectedImplementedby') as IName[]) || [];
				const selectedUmbrella: string[]
					= (viewModel.get('selectedUmbrella') as string[]) || [];
				const selectedStatuses: string[]
					= (viewModel.get('selectedStatuses') as string[]) || [];
				const selectedSources: string[]
					= (viewModel.get('selectedSources') as string[]) || [];
				const selectedRegions: IRegionUN[]
					= (viewModel.get('selectedRegions') as IRegionUN[]) || [];
				const selectedCountryTypes: string[]
					= (viewModel.get('selectedCountryTypes') as string[]) || [];
				const showGlobal = viewModel.get('showGlobal') as boolean;
				const showRegional = viewModel.get('showRegional') as boolean;
				const showUmbrellaSubprojects = viewModel.get('showUmbrellaSubprojects') as boolean;

				projectsDS.filter({
					logic: 'and',
					filters: [
						{
							field: 'isGlobal',
							operator(isGlobal: boolean, value: boolean) {
								if (!value) {
									return true;
								}

								return isGlobal;
							},
							value: showGlobal,
						},
						{
							field: 'umbrellaId',
							operator(umbrellaId: string, value: boolean) {
								if (value && umbrellaId === undefined) {
									return false;
								}

								return true;
							},
							value: showUmbrellaSubprojects,
						},
						{
							field: 'unRegions',
							operator(unRegions: IRegionUN[] | undefined, value: boolean) {
								if (!value) {
									return true;
								}

								return unRegions && unRegions.length > 0;
							},
							value: showRegional,
						},
						{
							field: 'keywords',
							operator: expandedFilter('name'),
							value: selectedTerms.map((term: IName) => term.name),
						},
						{
							field: 'beneficiaries',
							operator: expandedFilter('name'),
							value: selectedBeneficiaries.map(
								(beneficiary: IName) => beneficiary.name,
							),
						},
						{
							field: 'donors',
							operator: expandedFilter('name'),
							value: selectedDonors.map((donor: IName) => donor.name),
						},
						{
							field: 'implementedBy',
							operator: expandedFilter('name'),
							value: selectedImplementedby.map((item: IName) => item.name),
						},
						{
							field: 'umbrellaId',
							operator(umbrellaId: string, values: Array<{value: string; name: string}>) {
								if (values.length === 0) {
									return true;
								}

								if (umbrellaId === undefined) {
									return false;
								}

								if (values.map(item => item.value).includes(umbrellaId)) {
									return true;
								}

								return false;
							},
							value: selectedUmbrella,
						},
						{
							field: 'status',
							operator(status: string, values: any[]) {
								if (values.length === 0) {
									return true;
								}

								if (values.includes(status.toLowerCase())) {
									return true;
								}

								return false;
							},
							value: selectedStatuses.map((item: string) => item.toLowerCase()),
						},
						{
							field: 'type',
							operator(type: string, values: any[]) {
								if (values.length === 0) {
									return true;
								}

								if (values.includes(type.toLowerCase())) {
									return true;
								}

								return false;
							},
							value: selectedSources.map((item: string) => item.toLowerCase()),
						},
						{
							field: 'beneficiaries',
							operator(
								beneficiaries: ProjectData.ICountry[],
								values: string[],
							) {
								$('#brs-map').data('kendoBrsPlasticMapWidget').setDevelopments(values);
								if (values.length === 0) {
									return true;
								}

								for (const value of values) {
									for (const beneficiary of beneficiaries) {
										switch (value) {
											case 'Developing': {
												if (beneficiary.isDeveloping) {
													return true;
												}

												break;
											}

											case 'LDC': {
												if (beneficiary.isLDC) {
													return true;
												}

												break;
											}

											case 'SIDS': {
												if (beneficiary.isSIDS) {
													return true;
												}

												break;
											}

											default: {
												break;
											}
										}
									}
								}

								return false;
							},
							value: selectedCountryTypes,
						},
						{
							operator(item: IProject) {
								if (searchValue.length === 0) {
									return true;
								}

								return JSON.stringify(item).toLowerCase().includes(searchValue.toLowerCase());
							},
						},
						{
							operator(item: IProject) {
								$('#brs-map').data('kendoBrsPlasticMapWidget').setRegions(selectedRegions);
								if (selectedRegions.length === 0) {
									return true;
								}

								const unRegions = new Set(selectedRegions.map(region => region));
								return item.beneficiaries.some(country => unRegions.has(country.countryRegionUn))
								|| (item.unRegions ?? []).some(region => unRegions.has(region));
							},
						},
					],
				});
			},
			onSearchChange() {
				const sv = viewModel.get('searchValue') as string;
				if (sv.length > 3) {
					searchValue = sv;
					// eslint-disable-next-line @typescript-eslint/no-unsafe-call
					this.onChange();
				} else if (searchValue.length > 3) {
					searchValue = '';
					// eslint-disable-next-line @typescript-eslint/no-unsafe-call
					this.onChange();
				}
			},
			onViewChange: () => {
				const selectedMapView = viewModel.get('selectedMapView') as string;
				if (selectedMapView === 'budget') {
					this.mapWidget.setBudgetMap();
				} else {
					this.mapWidget.setProjecsCountMap();
				}

				// eslint-disable-next-line @typescript-eslint/no-floating-promises
				projectsDS.fetch();
			},
			onCollapsePanel(event: kendo.ui.PanelBarExpandEvent) {
				if (event.item) {
					$(event.item).find('>span').removeClass('k-selected');
				}
			},
		});

		const geojson = this.woptions?.geojson;
		$('#brs-map').kendoBrsPlasticMapWidget({dataSource: projectsDS, geojson});
		if (geojson) {
			const countriesDataSource: Array<{name: string; id: string}> = [];
			for (const feature of geojson.features) {
				if (feature.properties?.['name'] && feature.properties['id']) {
					countriesDataSource.push({
						name: feature.properties['name'] as string,
						id: feature.properties['id'] as string,
					});
				}
			}

			countriesDataSource.sort((a, b) => a.name.localeCompare(b.name));

			$('#brs-map-zoom-in').kendoDropDownList({
				dataSource: countriesDataSource,
				dataTextField: 'name',
				dataValueField: 'id',
				optionLabel: 'Zoom to country...',
				change(event: kendo.ui.DropDownListChangeEvent) {
					const map = $('#brs-map').data('kendoBrsPlasticMapWidget');
					map.zoomToCountry(event.sender.value());
					event.sender.value('');
				},
			});
		}

		this.mapWidget = $('#brs-map').data('kendoBrsPlasticMapWidget');
		this.mapWidget.setProjecsCountMap();

		$('#brs-projects-list').kendoBrsPlasticProjectsWidget({dataSource: projectsDS});
		$('#brs-projects-statistics').kendoBrsPlasticStatisticsWidget({dataSource: projectsDS});

		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		projectsDS.fetch();
		kendo.bind(this.element, viewModel);
	} // End constructor
} // End class
declare global {
	// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
	interface JQuery {
		kendoBrsPlasticProjectsDashboardWidget(options?: BrsPlasticProjectsDashboardWidgetOptions): JQuery;
	}
}

BrsBaseWidget.registerBrsWidget(
	BrsPlasticProjectsDashboardWidget,
	'BrsPlasticProjectsDashboardWidget',
);
